/* ════════════════════════════════════════════════════════════
   section-faq-accordion.js
   Controls smooth open/close of <details> accordion items.

   Why this exists:
   The browser removes [open] from <details> synchronously on
   summary click, which makes its content snap shut before any
   CSS transition can fire. This script intercepts the click,
   toggles a .is-open class for animation, and only removes the
   [open] attribute once the close transition has completed.
   ════════════════════════════════════════════════════════════ */

(function () {
  'use strict';

  // Matches the CSS transition duration (.faq__content); used as
  // a fallback timer in case transitionend doesn't fire.
  var TRANSITION_FALLBACK_MS = 400;

  function initItem(details) {
    if (!details || details.dataset.faqInit === '1') return;
    details.dataset.faqInit = '1';

    var summary = details.querySelector('summary');
    var content = details.querySelector('.faq__content');
    if (!summary || !content) return;

    var isAnimating = false;

    summary.addEventListener('click', function (e) {
      e.preventDefault();
      if (isAnimating) return;

      if (details.classList.contains('is-open')) {
        closeItem();
      } else {
        openItem();
      }
    });

    // Keyboard activation (Enter/Space) fires the click event on
    // <summary> natively, so the click handler above covers it.

    function openItem() {
      isAnimating = true;

      // Step 1: ensure the [open] attribute is set so the content
      // is rendered. Some browsers gate content rendering behind
      // [open] regardless of CSS overrides.
      details.setAttribute('open', '');

      // Step 2: force a reflow so the browser commits the
      // pre-animation state (grid-template-rows: 0fr) before we
      // change it.
      // eslint-disable-next-line no-unused-expressions
      content.offsetHeight;

      // Step 3: next frame, add the class — transition fires.
      requestAnimationFrame(function () {
        details.classList.add('is-open');
        onceTransitionEnd(content, 'grid-template-rows', function () {
          isAnimating = false;
        });
      });
    }

    function closeItem() {
      isAnimating = true;

      // Step 1: remove the class — grid-template-rows transitions
      // from 1fr back to 0fr.
      details.classList.remove('is-open');

      // Step 2: wait for the transition to finish, then remove the
      // [open] attribute so the element's semantic state matches
      // its visual state.
      onceTransitionEnd(content, 'grid-template-rows', function () {
        details.removeAttribute('open');
        isAnimating = false;
      });
    }
  }

  /**
   * Run `callback` exactly once when `propertyName` finishes
   * transitioning on `element`. Includes a setTimeout fallback in
   * case the transitionend event doesn't fire (e.g. element was
   * never in a transitionable state).
   */
  function onceTransitionEnd(element, propertyName, callback) {
    var done = false;

    function finish() {
      if (done) return;
      done = true;
      element.removeEventListener('transitionend', handler);
      callback();
    }

    function handler(e) {
      if (e.target !== element) return;
      if (e.propertyName !== propertyName) return;
      finish();
    }

    element.addEventListener('transitionend', handler);
    setTimeout(finish, TRANSITION_FALLBACK_MS);
  }

  function initAll(root) {
    (root || document).querySelectorAll('.faq__item').forEach(initItem);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll();
    });
  } else {
    initAll();
  }

  // Re-initialize when Shopify reloads a section in the theme editor.
  document.addEventListener('shopify:section:load', function (e) {
    initAll(e.target);
  });

  // When merchant selects a block in the editor, auto-open it so
  // its content is visible.
  document.addEventListener('shopify:block:select', function (e) {
    var item = e.target.closest && e.target.closest('.faq__item');
    if (!item) return;
    if (item.classList.contains('is-open')) return;
    var summary = item.querySelector('summary');
    if (summary) summary.click();
  });
})();